// Utility functions for handling the new ticket status system

export type TicketStatus = 'new' | 'in_progress' | 'in_progress_ai_agent' | 'in_progress_tier1' | 'in_progress_tier2' | 'in_progress_tier3' | 'escalated-awaiting-agent' | 'service' | 'closed-resolved' | 'closed-not-resolved' | 'closed-partially-resolved' | 'closed-no-feedback' | 'closed-by-agent-resolved' | 'abandoned';

// All closed status values
export const CLOSED_STATUSES: TicketStatus[] = [
  'closed-resolved', 
  'closed-not-resolved', 
  'closed-partially-resolved', 
  'closed-no-feedback',
  'closed-by-agent-resolved',
  'abandoned'
];

// Check if a status represents a closed ticket
export const isClosedStatus = (status: TicketStatus): boolean => {
  return CLOSED_STATUSES.includes(status);
};

// Backward compatibility aliases
export const isTicketClosed = isClosedStatus;
export const isTicketOpen = (status: TicketStatus): boolean => {
  return !isClosedStatus(status);
};

// Get the type of closure (resolved, not-resolved, etc.)
export const getClosureType = (status: TicketStatus): 'resolved' | 'not-resolved' | 'partial' | 'no-feedback' | 'agent-resolved' | 'abandoned' | null => {
    if (status === 'closed-resolved') return 'resolved';
    if (status === 'closed-not-resolved') return 'not-resolved';
    if (status === 'closed-partially-resolved') return 'partial';
    if (status === 'closed-no-feedback') return 'no-feedback';
    if (status === 'closed-by-agent-resolved') return 'agent-resolved';
    if (status === 'abandoned') return 'abandoned';
    return null;
};

// Get a human-readable status display name
export const getStatusDisplayName = (status: TicketStatus): string => {
  const statusMap: Record<TicketStatus, string> = {
    'new': 'New',
    'in_progress': 'In Progress',
    'in_progress_ai_agent': 'In Progress - AI Agent',
    'in_progress_tier1': 'In Progress - Tier 1',
    'in_progress_tier2': 'In Progress - Tier 2',
    'in_progress_tier3': 'In Progress - Tier 3',
    'escalated-awaiting-agent': 'Escalated – Awaiting Agent',
    'service': 'Service',
    'closed-resolved': 'Closed - Resolved',
    'closed-not-resolved': 'Closed - Not Resolved',
    'closed-partially-resolved': 'Closed - Partially Resolved',
    'closed-no-feedback': 'Closed - No Feedback',
    'closed-by-agent-resolved': 'Closed - Resolved by Agent',
    'abandoned': 'Abandoned'
  };
  
  return statusMap[status] || status;
};

// Get status color for badges/indicators
export const getStatusColor = (status: TicketStatus): string => {
    switch (status) {
        case 'new':
            return 'blue';
        case 'in_progress':
            return 'yellow';
        case 'in_progress_ai_agent':
            return 'cyan';
        case 'in_progress_tier1':
            return 'amber';
        case 'in_progress_tier2':
            return 'orange';
        case 'in_progress_tier3':
            return 'red';
        case 'escalated-awaiting-agent':
            return 'pink';
        case 'service':
            return 'indigo';
        case 'closed-resolved':
        case 'closed-by-agent-resolved':
            return 'green';
        case 'closed-not-resolved':
            return 'red';
        case 'closed-partially-resolved':
            return 'orange';
        case 'closed-no-feedback':
            return 'gray';
        case 'abandoned':
            return 'purple';
        default:
            return 'gray';
    }
};

// Get status color classes for UI (backward compatibility)
export const getStatusColorClasses = (status: TicketStatus): string => {
    switch (status) {
        case 'new':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        case 'in_progress':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        case 'in_progress_ai_agent':
            return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300';
        case 'in_progress_tier1':
            return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
        case 'in_progress_tier2':
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
        case 'in_progress_tier3':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        case 'escalated-awaiting-agent':
            return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300';
        case 'service':
            return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300';
        case 'closed-resolved':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        case 'closed-not-resolved':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        case 'closed-partially-resolved':
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
        case 'closed-no-feedback':
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
        case 'closed-by-agent-resolved':
            return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300';
        case 'abandoned':
            return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
};

// Get priority display name
export const getPriorityDisplayName = (priority: string): string => {
    const priorityMap: Record<string, string> = {
        'low': 'Low',
        'medium': 'Medium', 
        'high': 'High',
        'urgent': 'Urgent'
    };
    
    return priorityMap[priority] || priority;
};

// Get priority color
export const getPriorityColor = (priority: string): string => {
    switch (priority) {
        case 'low':
            return 'green';
        case 'medium':
            return 'yellow';
        case 'high':
            return 'orange';
        case 'urgent':
            return 'red';
        default:
            return 'gray';
    }
};

export const getStatusDisplay = (status: TicketStatus): string => {
  const displays: Record<TicketStatus, string> = {
    'new': 'New',
    'in_progress': 'In Progress',
    'in_progress_ai_agent': 'In Progress - AI Agent',
    'in_progress_tier1': 'In Progress - Tier 1',
    'in_progress_tier2': 'In Progress - Tier 2',
    'in_progress_tier3': 'In Progress - Tier 3',
    'escalated-awaiting-agent': 'Escalated – Awaiting Agent',
    'service': 'Service',
    'closed-resolved': 'Resolved',
    'closed-not-resolved': 'Not Resolved',
    'closed-partially-resolved': 'Partially Resolved',
    'closed-no-feedback': 'Closed (No Feedback)',
    'closed-by-agent-resolved': 'Resolved by Agent',
    'abandoned': 'Abandoned'
  };
  return displays[status] || status;
};

export const getStatusBadgeColor = (status: TicketStatus): string => {
  const colors: Record<TicketStatus, string> = {
    'new': 'bg-blue-100 text-blue-800',
    'in_progress': 'bg-yellow-100 text-yellow-800',
    'in_progress_ai_agent': 'bg-cyan-100 text-cyan-800',
    'in_progress_tier1': 'bg-amber-100 text-amber-800',
    'in_progress_tier2': 'bg-orange-100 text-orange-800',
    'in_progress_tier3': 'bg-red-100 text-red-800',
    'escalated-awaiting-agent': 'bg-pink-100 text-pink-800',
    'service': 'bg-indigo-100 text-indigo-800',
    'closed-resolved': 'bg-green-100 text-green-800',
    'closed-not-resolved': 'bg-red-100 text-red-800',
    'closed-partially-resolved': 'bg-orange-100 text-orange-800',
    'closed-no-feedback': 'bg-gray-100 text-gray-800',
    'closed-by-agent-resolved': 'bg-emerald-100 text-emerald-800',
    'abandoned': 'bg-purple-100 text-purple-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

// Ordered list of statuses for UI display (dropdown menus, filters, etc.)
export const STATUS_DISPLAY_ORDER: TicketStatus[] = [
  'new',
  'in_progress',
  'in_progress_ai_agent',
  'escalated-awaiting-agent',
  'in_progress_tier1',
  'in_progress_tier2',
  'in_progress_tier3',
  'service',
  'closed-resolved',
  'closed-not-resolved',
  'closed-partially-resolved',
  'closed-no-feedback',
  'closed-by-agent-resolved',
  'abandoned'
];

// Get status options for dropdowns in the correct display order
export const getStatusOptions = () => {
  return STATUS_DISPLAY_ORDER.map(status => ({
    value: status,
    label: getStatusDisplayName(status),
    color: getStatusColor(status)
  }));
}; 